"use client";

import React from "react";
import { BrainCircuit, Trash2, X } from "lucide-react";
import type { Node } from "@xyflow/react";
import type { IdeaNodeData, DbSchema } from "./types";
import { inputCls, labelCls, NODE_META } from "./constants";
import { ActionForm } from "./ActionForm";
import { NotesForm } from "./NotesForm";
import { PortsSection } from "./PortsSection";
import { LlmTestPanel } from "./LlmTestPanel";

export function EditPanel({
  node,
  onChange,
  onDelete,
  onClose,
  onGeneratePrompt,
  dbSchema,
}: {
  node: Node<IdeaNodeData>;
  onChange: (data: IdeaNodeData) => void;
  onDelete: () => void;
  onClose: () => void;
  onGeneratePrompt: () => void;
  dbSchema: DbSchema;
}) {
  const d = node.data;
  const m = NODE_META[d.nodeKind];
  const Icon = m.icon;
  const set = <K extends keyof IdeaNodeData>(key: K, val: IdeaNodeData[K]) =>
    onChange({ ...d, [key]: val });

  return (
    <div className="flex h-full flex-col">
      <div
        className={`flex shrink-0 items-center gap-2 border-b border-gray-100 px-5 py-4 dark:border-white/5 ${m.bg}`}
      >
        <Icon className={`h-5 w-5 ${m.color}`} />
        <span
          className={`text-xs font-bold uppercase tracking-wider ${m.color}`}
        >
          {m.label}
        </span>
        <div className="ml-auto flex gap-1">
          <button
            onClick={onGeneratePrompt}
            title="KI-Prompt für diesen Node"
            className="rounded-lg p-2 text-gray-400 hover:bg-amber-50 hover:text-amber-500 dark:hover:bg-amber-500/10"
          >
            <BrainCircuit className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            aria-label="Schritt löschen"
            className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-400 dark:hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            aria-label="Schritt schließen"
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left column: base fields + notes */}
          <div className="space-y-4">
            {d.nodeKind === "signal" && (
              <>
                <p className="text-sm text-gray-500">
                  Welche Interaktion löst diesen Flow aus, und welche Daten
                  liefert sie?
                </p>
                {(
                  [
                    ["event", "Ereignis", "message.submitted"],
                    ["source", "Quelle / Interaktion", "Chat · Senden-Button"],
                    [
                      "payload",
                      "Beispiel-Payload",
                      "Text oder JSON der Nutzereingabe",
                    ],
                  ] as const
                ).map(([key, label, placeholder]) => (
                  <label key={key} className="block">
                    <span className={labelCls}>{label}</span>
                    <textarea
                      rows={key === "payload" ? 4 : 1}
                      className={inputCls}
                      placeholder={placeholder}
                      value={d.signal?.[key] || ""}
                      onChange={(e) =>
                        set("signal", {
                          event: "",
                          source: "",
                          payload: "",
                          ...d.signal,
                          [key]: e.target.value,
                        })
                      }
                    />
                  </label>
                ))}
              </>
            )}
            {d.nodeKind === "state" && (
              <>
                <p className="text-sm text-gray-500">
                  Beschreibe einen fachlichen Zustand und die Bedingungen für
                  den Wechsel.
                </p>
                {(
                  [
                    [
                      "value",
                      "Zustandswert",
                      "idle, loading, answered, error…",
                    ],
                    [
                      "enterWhen",
                      "Eintrittsbedingung",
                      "Wann wird dieser Zustand erreicht?",
                    ],
                    [
                      "exitWhen",
                      "Austrittsbedingung",
                      "Welches Signal oder Ergebnis beendet ihn?",
                    ],
                  ] as const
                ).map(([key, label, placeholder]) => (
                  <label key={key} className="block">
                    <span className={labelCls}>{label}</span>
                    <textarea
                      rows={2}
                      className={inputCls}
                      placeholder={placeholder}
                      value={d.state?.[key] || ""}
                      onChange={(e) =>
                        set("state", {
                          value: "",
                          enterWhen: "",
                          exitWhen: "",
                          ...d.state,
                          [key]: e.target.value,
                        })
                      }
                    />
                  </label>
                ))}
              </>
            )}
            <div>
              <label className={labelCls}>Titel</label>
              <input
                autoFocus
                className={inputCls}
                placeholder="z.B. User ID"
                value={d.title}
                onChange={(e) => set("title", e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Beschreibung</label>
              <textarea
                className={`${inputCls} resize-none`}
                rows={4}
                placeholder="Kurze Erklärung…"
                value={d.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
            <NotesForm notes={d.notes} onChange={(n) => set("notes", n)} />
          </div>
          {/* Right column: type-specific form + ports */}
          <div className="space-y-4">
            {d.nodeKind === "action" && d.actionPayload && (
              <ActionForm
                p={d.actionPayload}
                onChange={(p) => set("actionPayload", p)}
              />
            )}
            {d.nodeKind === "action" && (
              <PortsSection
                label="Ports"
                ports={d.ports ?? []}
                onChange={(ports) => set("ports", ports)}
                accentColor="amber"
                dbSchema={dbSchema}
              />
            )}
            {d.nodeKind === "action" &&
              d.actionPayload?.subtype === "llm_prompt" && (
                <LlmTestPanel data={d} onChange={onChange} />
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
